/**
 * KALKI Admin Dashboard 1.0 — root shell + sign-in gate.
 *
 * One file, one router, six module pages (Overview, Users, Life Reports,
 * Credits, AI Activity, System Health).  Reads only — no mutation routes
 * mounted here. Google-OAuth-only admin sign-in (via the existing
 * /auth/me flow) + server-side allow-list check inside every /api/admin/*
 * endpoint.
 *
 * Design: Apple-minimal, white + graphite, indigo + aqua accents, slim
 * left sidebar on desktop, collapsible drawer on mobile.
 */
import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Shield, Users, FileText, Wallet, Activity, HeartPulse, LayoutDashboard, BarChart3, Menu, X, LogOut } from "lucide-react";
import { api, useAuth } from "@/hooks/useAuth";
import AdminOverview from "./modules/AdminOverview";
import AdminUsers from "./modules/AdminUsers";
import AdminReports from "./modules/AdminReports";
import AdminCredits from "./modules/AdminCredits";
import AdminAI from "./modules/AdminAI";
import AdminHealth from "./modules/AdminHealth";
import AdminInnerMirror from "./modules/AdminInnerMirror";
import AdminMis from "./modules/AdminMis";
import AdminDailyBlessings from "./modules/AdminDailyBlessings";

const INK = "#1C1C1C";
const INDIGO = "#5D00FF";
const AQUA = "#00C2CB";
const MUTE = "#676773";
const BORDER = "#E8E6EF";

const NAV = [
  { to: "/admin",         label: "Overview",      icon: LayoutDashboard, end: true },
  { to: "/admin/users",   label: "Users",         icon: Users },
  { to: "/admin/reports", label: "Life Reports",  icon: FileText },
  { to: "/admin/credits", label: "Credits",       icon: Wallet },
  { to: "/admin/ai",      label: "AI Activity",   icon: Activity },
  { to: "/admin/health",       label: "System Health",  icon: HeartPulse },
  { to: "/admin/inner-mirror", label: "Inner Mirror",   icon: Shield },
  { to: "/admin/mis",          label: "MIS",            icon: BarChart3 },
  { to: "/admin/daily-blessings", label: "Daily Blessing", icon: HeartPulse },
];

export default function AdminApp() {
  const { user, loading, logout } = useAuth() || {};
  const [isAdmin, setIsAdmin] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    // Prevent search engines from indexing /admin
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch (_) {} };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { setIsAdmin(false); return; }
    (async () => {
      // Ask the server authoritatively; the allow-list lives server-side.
      try {
        const { data } = await api.get("/admin/metrics");
        setIsAdmin(!!data);
      } catch (e) {
        setIsAdmin(false);
      }
    })();
  }, [user, loading]);

  if (loading || isAdmin === null) {
    return <Centered><Loader2 className="spin" size={22} color={INDIGO} /></Centered>;
  }
  if (!user) return <AdminSignIn />;
  if (!isAdmin) return <AccessDenied email={user.email} onLogout={logout} />;

  return <AdminShell user={user} onLogout={logout} />;
}

function AdminShell({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div data-testid="admin-shell" style={{
      minHeight: "100vh", background: "#FAFAFC", color: INK,
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      display: "flex",
    }}>
      {/* Sidebar — desktop */}
      <aside className="admin-sidebar" style={{
        width: 220, borderRight: `1px solid ${BORDER}`, background: "white",
        padding: "22px 14px", position: "sticky", top: 0, height: "100vh",
      }}>
        <AdminBrand />
        <nav style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px", borderRadius: 10,
                fontSize: 13, fontWeight: 500,
                color: isActive ? "white" : INK,
                background: isActive ? INDIGO : "transparent",
                textDecoration: "none",
                transition: "background 140ms ease",
              })}>
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ position: "absolute", bottom: 20, left: 14, right: 14 }}>
          <div style={{ fontSize: 11, color: MUTE, marginBottom: 6 }}>Signed in as</div>
          <div style={{ fontSize: 12, color: INK, wordBreak: "break-all", marginBottom: 10 }}>
            {user?.email}
          </div>
          <button onClick={onLogout} data-testid="admin-logout"
            style={{ display: "inline-flex", alignItems: "center", gap: 6,
                     fontSize: 11.5, color: MUTE, background: "transparent",
                     border: `1px solid ${BORDER}`, padding: "6px 10px",
                     borderRadius: 999, cursor: "pointer" }}>
            <LogOut size={12} /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="admin-mobile-topbar" style={{
        display: "none", position: "sticky", top: 0, zIndex: 20,
        background: "white", borderBottom: `1px solid ${BORDER}`,
        padding: "12px 16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setOpen(true)} aria-label="Open menu"
                   data-testid="admin-mobile-menu-open"
                   style={{ background: "transparent", border: 0, cursor: "pointer" }}>
            <Menu size={20} />
          </button>
          <AdminBrand compact />
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100,
          background: "rgba(10,8,30,0.5)",
          backdropFilter: "blur(10px)",
        }} onClick={() => setOpen(false)} data-testid="admin-mobile-drawer-backdrop">
          <aside style={{
            width: 240, height: "100vh", background: "white",
            padding: 18, display: "flex", flexDirection: "column",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <AdminBrand />
              <button onClick={() => setOpen(false)} aria-label="Close"
                       style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                <X size={18} />
              </button>
            </div>
            <nav style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 4 }}>
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  style={({ isActive }) => ({
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: 10,
                    fontSize: 13, color: isActive ? "white" : INK,
                    background: isActive ? INDIGO : "transparent",
                    textDecoration: "none",
                  })}>
                  <n.icon size={15} /> {n.label}
                </NavLink>
              ))}
            </nav>
            <button onClick={onLogout}
              style={{ marginTop: "auto", padding: "10px 12px",
                       background: "transparent", border: `1px solid ${BORDER}`,
                       borderRadius: 10, cursor: "pointer", fontSize: 12.5 }}>
              Log out ({user?.email})
            </button>
          </aside>
        </div>
      )}

      <main style={{ flex: 1, padding: "26px 32px",
                      maxWidth: "100%", minWidth: 0, overflowX: "hidden" }}>
        <Routes>
          <Route path="/"         element={<AdminOverview />} />
          <Route path="/users"    element={<AdminUsers />} />
          <Route path="/reports"  element={<AdminReports />} />
          <Route path="/credits"  element={<AdminCredits />} />
          <Route path="/ai"       element={<AdminAI />} />
          <Route path="/health"   element={<AdminHealth />} />
          <Route path="/inner-mirror" element={<AdminInnerMirror />} />
          <Route path="/mis"      element={<AdminMis />} />
          <Route path="/daily-blessings" element={<AdminDailyBlessings />} />
          <Route path="*"         element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .admin-sidebar { display: none !important; }
          .admin-mobile-topbar { display: block !important; }
          main { padding: 18px 16px !important; }
        }
      `}</style>
    </div>
  );
}

function AdminBrand({ compact }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: `linear-gradient(135deg, ${INDIGO}, ${AQUA})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "white",
      }}>
        <Shield size={14} />
      </div>
      {!compact && (
        <div style={{ lineHeight: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: INK }}>KALKI</div>
          <div style={{ fontSize: 10, color: MUTE, letterSpacing: "0.2em", marginTop: 2 }}>
            ADMIN · 1.0
          </div>
        </div>
      )}
    </div>
  );
}

function AdminSignIn() {
  const signInWithGoogle = async () => {
    // Reuse the existing Emergent-managed Google OAuth flow — the public
    // sign-in button lives on the homepage; redirect there, then the
    // allow-list check on /api/admin/* decides the rest.
    //
    // CRITICAL: stash the intended destination so AuthCallback bounces
    // the admin back to the exact deep-link they tried to open (/admin,
    // /admin/mis, /admin/mis/users, …). Without this stash, AuthCallback
    // defaults to /app/chat and dumps the admin on the user app — the
    // reported "/admin redirects to main app" bug.
    try {
      const intent = window.location.pathname + (window.location.search || "");
      if (intent && intent.startsWith("/admin")) {
        sessionStorage.setItem("kalki_post_login_next", intent);
      } else {
        sessionStorage.setItem("kalki_post_login_next", "/admin");
      }
    } catch (_) {}
    const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/admin")}`;
    window.location.href = url;
  };
  return (
    <div data-testid="admin-signin" style={{
      minHeight: "100vh", background: "#FAFAFC",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
    }}>
      <div style={{
        maxWidth: 400, width: "100%",
        background: "white", borderRadius: 20, padding: 32,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 24px 64px rgba(10,8,30,0.08)",
      }}>
        <AdminBrand />
        <h1 style={{ marginTop: 20, fontSize: 22, color: INK, fontWeight: 600 }}>
          KALKI Admin
        </h1>
        <p style={{ fontSize: 13, color: MUTE, lineHeight: 1.55, marginBottom: 20 }}>
          Sign in with your authorized Google account to access the admin
          dashboard. Access is restricted to the server-side allow-list.
        </p>
        <button onClick={signInWithGoogle}
          data-testid="admin-signin-google"
          style={{
            width: "100%", padding: "12px 16px", borderRadius: 999,
            background: INDIGO, color: "white", border: 0,
            fontSize: 13, fontWeight: 500, cursor: "pointer",
          }}>
          Sign in with Google
        </button>
        <div style={{ marginTop: 14, fontSize: 11, color: MUTE,
                       textAlign: "center" }}>
          Non-admin accounts will see an access-denied screen.
        </div>
      </div>
    </div>
  );
}

function AccessDenied({ email, onLogout }) {
  return (
    <div data-testid="admin-access-denied" style={{
      minHeight: "100vh", background: "#FAFAFC",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
    }}>
      <div style={{
        maxWidth: 420, width: "100%", textAlign: "center",
        background: "white", borderRadius: 20, padding: 32,
        border: `1px solid ${BORDER}`,
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: INK }}>Access denied</h1>
        <p style={{ fontSize: 13, color: MUTE, marginTop: 10 }}>
          Your account <strong>{email}</strong> is not authorized for the
          KALKI admin dashboard.
        </p>
        <button onClick={onLogout}
          style={{ marginTop: 18, padding: "10px 16px", borderRadius: 999,
                   background: INDIGO, color: "white", border: 0,
                   fontSize: 13, cursor: "pointer" }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex",
                   alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  );
}
